import Router from "next/router";
import React from "react";

/**
 * Back closes the top sheet, never the page.
 *
 * An open sheet owns one history entry: a copy of the page's entry (same URL,
 * same Next key) marked `__sheet: <depth>`. Back pops it, and the pop closes
 * every sheet deeper than the entry it lands on.
 *
 * Closing a sheet any other way (✕, swipe, a chosen item) never moves
 * history; the entry is marked `__spent` in place. That keeps a close from
 * racing whatever the chosen item does next. Next's pushState and
 * replaceState run through a wrapper here, and a spent entry is absorbed by
 * the next of them (a push replaces it; a replace to another page replaces
 * the page under it too), reused by the next sheet, or stepped over by the
 * next Back.
 *
 * beforePopState keeps Next from routing for a pop between a sheet's entry
 * and its page, so those pops never refetch the page or fire route events.
 *
 * A sheet whose open state is a URL view (usePushedView) takes no entry:
 * the URL's entry is already its own (BottomSheet's `urlView`).
 *
 * Vercel's insights script patches pushState after this does (it loads from
 * an effect in Root, after the layout's sheets mount), so a push absorbed
 * here still reaches it as a push.
 *
 * Known gaps: Forward onto a dead entry bounces back. Next's constructor
 * re-keys the current entry on load, before any of this runs, so a reload
 * with a sheet open leaves one Back that only reaches the page. A sheet that
 * stays open through a navigation it started loses its entry; sheets close
 * when they navigate.
 */

type Sheet = { depth: number; close: () => void; hasEntry: boolean };
type SheetState = {
  __sheet?: number;
  __spent?: boolean;
  __N?: boolean;
  as?: string;
  [key: string]: unknown;
};
type Hop = {
  kind: "skip" | "leave" | "absorb";
  /** Where the step should land, when it's known. */
  depth?: number;
  as?: string;
  absorb?: { state: unknown; url?: string | URL | null };
};

const stack: Sheet[] = [];
let installed = false;
// Fast Refresh evaluates this module again without unloading the old copy,
// whose listeners and wrappers stay attached. The newest copy owns the
// history; older ones see they don't and pass straight through.
const self = {};
const OWNER = "__apolloSheetHistory";
const active = () =>
  typeof window !== "undefined" &&
  (window as unknown as Record<string, unknown>)[OWNER] === self;
let hop: Hop | null = null;
// The entry we're on, as of the last write or pop this module saw.
let here: SheetState | null = null;
// Pops someone else asked for (usePushedView's close) that haven't landed.
let externalPops = 0;
let waitingForPops: Sheet[] = [];
const popListeners = new Set<(state: unknown) => void>();
let push: History["pushState"];
let replace: History["replaceState"];

const depthOf = (state: unknown): number => {
  const depth = (state as SheetState | null)?.__sheet;
  return typeof depth === "number" ? depth : 0;
};
const isOurs = (state: unknown) => depthOf(state) > 0;
const liveAt = (depth: number) =>
  stack.some((sheet) => sheet.hasEntry && sheet.depth === depth);
/** An entry of ours whose sheet is no longer open on it. */
const isDead = (state: unknown) =>
  isOurs(state) &&
  (!!(state as SheetState).__spent || !liveAt(depthOf(state)));

const current = () => window.history.state as SheetState | null;
const sync = () => {
  here = current();
};

const closeAbove = (depth: number) => {
  for (let i = stack.length - 1; i >= 0; i -= 1) {
    const sheet = stack[i];
    if (!sheet.hasEntry || sheet.depth <= depth) continue;
    stack.splice(i, 1);
    sheet.close();
  }
};

const startHop = (next: Hop, delta: number) => {
  hop = next;
  window.history.go(delta);
};

// Next calls these from changeState, so a navigation started from a sheet's
// entry is seen when it writes history, whatever order React and the router
// reached that point in.
const wrappedPush: History["pushState"] = (state, unused, url) => {
  if (!active()) return push.call(window.history, state, unused, url);
  if ((state as SheetState)?.__N && isOurs(current())) {
    stack.forEach((sheet) => (sheet.hasEntry = false));
    replace.call(window.history, state, unused, url);
  } else {
    push.call(window.history, state, unused, url);
  }
  sync();
};

const wrappedReplace: History["replaceState"] = (state, unused, url) => {
  if (!active()) return replace.call(window.history, state, unused, url);
  const entry = current();
  const next = state as SheetState;
  if (!next?.__N || !isOurs(entry)) {
    replace.call(window.history, state, unused, url);
    sync();
    return;
  }
  if (next.as === entry.as) {
    replace.call(
      window.history,
      { ...next, __sheet: entry.__sheet, __spent: entry.__spent },
      unused,
      url
    );
    sync();
    return;
  }
  // A replace to another page stands in for the page under the sheets too.
  replace.call(window.history, state, unused, url);
  sync();
  stack.forEach((sheet) => (sheet.hasEntry = false));
  startHop(
    { kind: "absorb", depth: 0, as: entry.as, absorb: { state, url } },
    -depthOf(entry)
  );
};

const notify = (state: unknown) => {
  popListeners.forEach((listener) => listener(state));
  return true;
};

// Runs inside Next's popstate handler, before onPopState below.
const beforePopState = (popped: unknown) => {
  if (!active()) return true;
  const state = popped as SheetState | null;
  if (hop) {
    // Our own step, landing where it was headed.
    if (hop.as !== undefined && state?.as === hop.as) return false;
    // A pop to another page (a step that leaves, or a Back of the user's
    // that overtook ours): Next has to route for it.
    return state?.as !== Router.asPath && notify(state);
  }
  if ((isOurs(here) || isOurs(state)) && state?.as === Router.asPath) {
    return false;
  }
  return notify(state);
};

const onPopState = () => {
  if (!active()) return;
  const state = current();
  const left = here;
  const leftDead = isDead(left);
  here = state;
  const landed = depthOf(state);

  if (hop) {
    const step = hop;
    hop = null;
    if (step.depth === undefined || step.depth === landed) {
      if (step.absorb) {
        replace.call(window.history, step.absorb.state, "", step.absorb.url);
        sync();
      }
      closeAbove(landed);
      if (isDead(current())) skip(landed);
      return;
    }
    // Not where our step was headed: a Back of the user's overtook it.
  }

  if (externalPops > 0) {
    externalPops -= 1;
    closeAbove(landed);
    if (!externalPops) takeWaitingEntries();
    return;
  }

  closeAbove(landed);
  if (isDead(state)) {
    if (leftDead && depthOf(left) > landed && left?.as === state?.as) {
      // Back from a spent entry onto another dead one (a mode whose sheet
      // closed above it): Back is from what showed under both, the nearest
      // sheet still open, or else the page itself.
      const live = stack.reduce(
        (top, sheet) =>
          sheet.hasEntry && sheet.depth < landed ? Math.max(top, sheet.depth) : top,
        0
      );
      startHop(
        live ? { kind: "leave", depth: live - 1 } : { kind: "leave" },
        -(landed - live + 1)
      );
      return;
    }
    // Back from another screen, or Forward, onto an entry whose sheet is
    // gone: keep going back to the page.
    skip(landed);
    return;
  }
  if (leftDead && depthOf(left) > landed && left?.as === state?.as) {
    // Back from a spent entry is Back from whatever showed under it.
    startHop({ kind: "leave", depth: landed ? landed - 1 : undefined }, -1);
  }
};

const skip = (landed: number) =>
  startHop({ kind: "skip", depth: landed - 1, as: current()?.as }, -1);

const onRouteChangeEnd = () => sync();

/** Idempotent; every sheet's first render calls it. */
export const installSheetHistory = () => {
  if (installed || typeof window === "undefined") return;
  installed = true;
  (window as unknown as Record<string, unknown>)[OWNER] = self;
  push = window.history.pushState;
  replace = window.history.replaceState;
  window.history.pushState = wrappedPush;
  window.history.replaceState = wrappedReplace;
  sync();
  window.addEventListener("popstate", onPopState);
  Router.beforePopState(beforePopState);
  Router.events.on("routeChangeComplete", onRouteChangeEnd);
  Router.events.on("routeChangeError", onRouteChangeEnd);
};

/**
 * beforePopState is a single global slot, and the sheets own it. Anything
 * else that needs to hear of a pop Next will route for subscribes here.
 */
export const onBeforePopState = (listener: (state: unknown) => void) => {
  installSheetHistory();
  popListeners.add(listener);
  return () => {
    popListeners.delete(listener);
  };
};

/**
 * Called just before a history.back() someone else makes, so a sheet opened
 * in the same moment takes its entry after that pop instead of under it.
 */
export const expectExternalPop = () => {
  installSheetHistory();
  externalPops += 1;
  setTimeout(() => {
    if (!externalPops) return;
    externalPops = 0;
    takeWaitingEntries();
  }, 1000);
};


/** How many sheet entries sit above the page's own entry right now. */
export const sheetEntriesAbovePage = () =>
  typeof window === "undefined" ? 0 : depthOf(current());

const takeEntry = (sheet: Sheet) => {
  if (!stack.includes(sheet)) return;
  const entry = current() ?? {};
  if (isDead(entry)) {
    // Reuse the spent entry in place, keeping its depth.
    sheet.depth = depthOf(entry);
    replace.call(window.history, { ...entry, __spent: false }, "");
  } else {
    sheet.depth = depthOf(entry) + 1;
    push.call(window.history, { ...entry, __sheet: sheet.depth }, "");
  }
  sheet.hasEntry = true;
  sync();
};

const takeWaitingEntries = () => {
  const waiting = waitingForPops;
  waitingForPops = [];
  waiting.forEach(takeEntry);
};

/**
 * Gives an opening sheet its history entry. `close` is called when Back pops
 * it; the returned release is called when the sheet closes any other way.
 */
export const openSheetEntry = (close: () => void): (() => void) => {
  if (typeof window === "undefined") return () => undefined;
  installSheetHistory();
  const sheet: Sheet = { depth: 0, close, hasEntry: false };
  stack.push(sheet);
  if (externalPops) waitingForPops.push(sheet);
  else takeEntry(sheet);
  return () => release(sheet);
};

const release = (sheet: Sheet) => {
  const index = stack.indexOf(sheet);
  if (index < 0) return;
  const removed = stack.splice(index);
  // Sheets opened from this one close with it.
  removed.slice(1).forEach((child) => child.close());
  if (!removed.some((s) => s.hasEntry)) return;
  const entry = current();
  if (isOurs(entry) && depthOf(entry) >= sheet.depth && !entry.__spent) {
    replace.call(window.history, { ...entry, __spent: true }, "");
    sync();
  }
};

/**
 * The contract for anything that opens like a sheet: while `open`, it owns a
 * history entry, and Back calls `onClose`.
 */
export const useSheetHistory = (open: boolean, onClose: () => void) => {
  const closeFromHistory = React.useEffectEvent(onClose);
  React.useEffect(() => {
    installSheetHistory();
  }, []);
  React.useEffect(() => {
    if (!open) return;
    return openSheetEntry(closeFromHistory);
  }, [open]);
};

/** For tests: put history back and forget every sheet and listener. */
export const resetSheetHistory = () => {
  stack.length = 0;
  waitingForPops = [];
  hop = null;
  here = null;
  externalPops = 0;
  popListeners.clear();
  if (installed && typeof window !== "undefined") {
    if (active()) delete (window as unknown as Record<string, unknown>)[OWNER];
    // Unhook only if nothing has wrapped us since; otherwise stay in the
    // chain as a pass-through.
    if (window.history.pushState === wrappedPush) window.history.pushState = push;
    if (window.history.replaceState === wrappedReplace) {
      window.history.replaceState = replace;
    }
    window.removeEventListener("popstate", onPopState);
    Router.events.off("routeChangeComplete", onRouteChangeEnd);
    Router.events.off("routeChangeError", onRouteChangeEnd);
    Router.beforePopState(() => true);
  }
  installed = false;
};
